import React from 'react';
import {
  Wallet,
  Gift,
  Ticket,
  Clock,
  Home,
  TrendingUp,
  Users,
  Settings,
  Menu,
  Bell,
  ChevronRight,
} from 'lucide-react';
import { useApp } from '@/context/AppContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

// Mobile Bottom Navigation for Customer
export function MobileBottomNav() {
  const { activeTab, setActiveTab } = useApp();

  const navItems = [
    { id: 'home', label: 'Home', icon: Home },
    { id: 'earn', label: 'Earn', icon: TrendingUp },
    { id: 'redeem', label: 'Redeem', icon: Gift },
    { id: 'vouchers', label: 'Vouchers', icon: Ticket },
    { id: 'history', label: 'History', icon: Clock },
  ];

  return (
    <nav className="fixed bottom-0 left-0 right-0 bg-card border-t border-border z-50 lg:hidden">
      <div className="flex items-center justify-around py-2 px-2">
        {navItems.map((item) => {
          const Icon = item.icon;
          const isActive = activeTab === item.id;
          return (
            <button
              key={item.id}
              onClick={() => setActiveTab(item.id)}
              className={cn(
                'flex flex-col items-center justify-center py-2 px-3 rounded-lg transition-colors min-w-[60px]',
                isActive
                  ? 'text-primary bg-primary/5'
                  : 'text-muted-foreground hover:text-foreground'
              )}
            >
              <Icon className={cn('h-5 w-5 mb-1', isActive && 'text-primary')} />
              <span className={cn('text-xs font-medium', isActive && 'text-primary')}>
                {item.label}
              </span>
            </button>
          );
        })}
      </div>
    </nav>
  );
}

// Mobile Header
export function MobileHeader({ title, showBack, onBack }) {
  const { addNotification } = useApp();

  return (
    <header className="sticky top-0 z-40 bg-card/95 backdrop-blur-sm border-b border-border">
      <div className="flex items-center justify-between px-4 py-3">
        <div className="flex items-center gap-3">
          {showBack ? (
            <button
              onClick={onBack}
              className="p-2 -ml-2 rounded-lg hover:bg-muted transition-colors"
            >
              <ChevronRight className="h-5 w-5 rotate-180" />
            </button>
          ) : (
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
                <span className="text-primary-foreground font-bold text-sm">P</span>
              </div>
            </div>
          )}
          <h1 className="font-display font-semibold text-lg text-foreground">
            {title || 'Physiomobile'}
          </h1>
        </div>
        <button
          onClick={() => addNotification({ type: 'info', message: 'No new notifications' })}
          className="p-2 rounded-lg hover:bg-muted transition-colors relative"
        >
          <Bell className="h-5 w-5 text-muted-foreground" />
          <span className="absolute top-1 right-1 w-2 h-2 bg-primary rounded-full"></span>
        </button>
      </div>
    </header>
  );
}

// Admin Sidebar
export function AdminSidebar() {
  const { activeTab, setActiveTab } = useApp();

  const menuItems = [
    { id: 'dashboard', label: 'Dashboard', icon: Home },
    { id: 'rules', label: 'Points Rules', icon: Settings },
    { id: 'catalog', label: 'Redemption Catalog', icon: Gift },
    { id: 'campaigns', label: 'Campaigns', icon: TrendingUp },
    { id: 'partners', label: 'Partners', icon: Users },
    { id: 'vouchers', label: 'Vouchers Monitor', icon: Ticket },
    { id: 'fraud', label: 'Fraud Review', icon: Clock },
    { id: 'reports', label: 'Reports', icon: Wallet },
  ];

  return (
    <aside className="hidden lg:flex flex-col w-64 bg-card border-r border-border h-screen sticky top-0">
      <div className="p-6 border-b border-border">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-primary rounded-xl flex items-center justify-center">
            <span className="text-primary-foreground font-bold text-lg">P</span>
          </div>
          <div>
            <h1 className="font-display font-bold text-lg">Physiomobile</h1>
            <p className="text-xs text-muted-foreground">Loyalty Admin</p>
          </div>
        </div>
      </div>
      <nav className="flex-1 p-4 overflow-y-auto custom-scrollbar">
        <ul className="space-y-1">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const isActive = activeTab === item.id;
            return (
              <li key={item.id}>
                <button
                  onClick={() => setActiveTab(item.id)}
                  className={cn(
                    'w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all',
                    isActive
                      ? 'bg-primary/10 text-primary border-l-2 border-primary'
                      : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                  )}
                >
                  <Icon className="h-5 w-5" />
                  {item.label}
                </button>
              </li>
            );
          })}
        </ul>
      </nav>
      <div className="p-4 border-t border-border">
        <div className="flex items-center gap-3 px-4 py-2">
          <div className="w-8 h-8 bg-muted rounded-full flex items-center justify-center">
            <span className="text-sm font-medium">A</span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate">Admin User</p>
            <p className="text-xs text-muted-foreground">admin@physiomobile.com</p>
          </div>
        </div>
      </div>
    </aside>
  );
}

// Admin Header
export function AdminHeader({ title }) {
  const { addNotification } = useApp();

  return (
    <header className="sticky top-0 z-40 bg-card/95 backdrop-blur-sm border-b border-border">
      <div className="flex items-center justify-between px-6 py-4">
        <div className="flex items-center gap-4">
          <button className="lg:hidden p-2 rounded-lg hover:bg-muted">
            <Menu className="h-5 w-5" />
          </button>
          <h1 className="font-display font-semibold text-xl">{title}</h1>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => addNotification({ type: 'info', message: 'No new alerts' })}
          >
            <Bell className="h-5 w-5" />
          </Button>
          <Badge variant="success" className="hidden sm:flex">
            System Healthy
          </Badge>
        </div>
      </div>
    </header>
  );
}

// View Switcher (for demo purposes)
export function ViewSwitcher() {
  const { currentView, setCurrentView, setActiveTab } = useApp();

  const handleViewChange = (view) => {
    setCurrentView(view);
    if (view === 'customer') setActiveTab('home');
    else if (view === 'admin') setActiveTab('dashboard');
    else if (view === 'partner') setActiveTab('home');
  };

  return (
    <div className="fixed top-4 right-4 z-50 flex gap-2 bg-card/95 backdrop-blur-sm border border-border rounded-lg p-1 shadow-lg">
      <Button
        variant={currentView === 'customer' ? 'default' : 'ghost'}
        size="sm"
        onClick={() => handleViewChange('customer')}
      >
        Customer
      </Button>
      <Button
        variant={currentView === 'admin' ? 'default' : 'ghost'}
        size="sm"
        onClick={() => handleViewChange('admin')}
      >
        Admin
      </Button>
      <Button
        variant={currentView === 'partner' ? 'default' : 'ghost'}
        size="sm"
        onClick={() => handleViewChange('partner')}
      >
        Partner
      </Button>
    </div>
  );
}
